import { motion } from "framer-motion";
import {
  ShieldCheck,
  Truck,
  HardHat,
  Users,
  CheckCircle2,
  RefreshCw,
  Clock,
  Phone,
  ArrowRight,
} from "lucide-react";
import { useI18n } from "@/context/I18nContext";

const ICON_MAP = {
  shield: ShieldCheck,
  truck: Truck,
  hardhat: HardHat,
  users: Users,
  check: CheckCircle2,
  refresh: RefreshCw,
};

export default function WorkforceAvailable() {
  const { t, dir } = useI18n();
  const isRtl = dir === "rtl";
  const w = t.workforceAvailable;

  return (
    <section
      id="workforce-available"
      className="relative bg-secondary text-secondary-foreground overflow-hidden"
      data-testid="workforce-available-section"
    >
      {/* Ambient gold blobs */}
      <div className="pointer-events-none absolute -top-40 left-1/4 w-[480px] h-[480px] rounded-full bg-primary/15 blur-3xl" />
      <div className="pointer-events-none absolute bottom-0 -right-40 w-[420px] h-[420px] rounded-full bg-primary/10 blur-3xl" />

      <div className="container-page section-y relative">
        {/* Hero band */}
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-end mb-16">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-7"
          >
            <div className="flex items-center gap-3">
              <span className="h-px w-10 bg-primary" />
              <span className="overline">{w.eyebrow}</span>
            </div>
            <h2 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.02] tracking-tight">
              {w.heading1} <br />
              <span className="text-primary">{w.heading1Accent}</span>
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="lg:col-span-5"
          >
            <p className="text-secondary-foreground/75 leading-relaxed text-base md:text-lg">
              {w.lead}
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <a
                href="#contact"
                className="inline-flex items-center gap-3 px-6 py-3 bg-primary text-primary-foreground text-sm font-semibold uppercase tracking-[0.18em] hover:bg-primary/90 transition-colors group"
                data-testid="workforce-available-cta"
              >
                {w.cta}
                <ArrowRight className={`h-4 w-4 transition-transform ${isRtl ? "scale-x-[-1] group-hover:-translate-x-1" : "group-hover:translate-x-1"}`} />
              </a>
              <a
                href="tel:+966500338215"
                className="inline-flex items-center gap-3 px-6 py-3 border border-white/15 text-secondary-foreground text-sm font-semibold uppercase tracking-[0.18em] hover:border-primary hover:text-primary transition-colors"
                data-testid="workforce-available-call"
              >
                <Phone className="h-4 w-4" />
                {w.ctaSecondary}
              </a>
            </div>
          </motion.div>
        </div>

        {/* Big 1,000+ presentation block + SLA */}
        <div className="grid lg:grid-cols-12 gap-px bg-white/10 border border-white/10 mb-12">
          {/* Mega counter */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-7 bg-secondary p-10 md:p-14 relative overflow-hidden"
            data-testid="workforce-mega-counter"
          >
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 opacity-[0.06]"
              style={{
                backgroundImage:
                  "linear-gradient(transparent 95%, currentColor 95%), linear-gradient(90deg, transparent 95%, currentColor 95%)",
                backgroundSize: "44px 44px",
              }}
            />
            <div className="relative">
              <div className="text-xs uppercase tracking-[0.22em] text-primary font-bold">
                {w.slaTitle}
              </div>
              <div className="mt-4 text-[80px] sm:text-[120px] lg:text-[160px] font-black leading-[0.85] tracking-tighter text-primary">
                1,000<span className="text-secondary-foreground">+</span>
              </div>
              <div className="mt-4 text-lg md:text-xl text-secondary-foreground/70 max-w-xl leading-relaxed">
                {w.slaText}
              </div>
            </div>
          </motion.div>

          {/* Includes grid */}
          <div className="lg:col-span-5 grid grid-cols-2 gap-px bg-white/10">
            {w.includes.map((item, i) => {
              const Icon = ICON_MAP[item.icon] || CheckCircle2;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.4, delay: 0.1 + i * 0.06 }}
                  className="bg-secondary p-5 md:p-6 hover:bg-secondary/60 transition-colors group"
                  data-testid={`workforce-include-${i}`}
                >
                  <div className="w-9 h-9 grid place-items-center border border-white/15 group-hover:border-primary transition-colors">
                    <Icon className="h-4 w-4 text-primary" />
                  </div>
                  <div className="mt-4 text-sm font-semibold leading-snug">
                    {item.label}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Trades */}
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-6"
          >
            <h3 className="text-2xl md:text-3xl font-black leading-tight">
              {w.tradesTitle}
            </h3>
          </motion.div>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="lg:col-span-6 text-secondary-foreground/65 leading-relaxed"
          >
            {w.tradesSubtitle}
          </motion.p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
          {w.trades.map((trade, i) => (
            <motion.article
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.45, delay: i * 0.05 }}
              className="group relative bg-secondary p-6 md:p-7 hover:bg-background hover:text-foreground transition-colors duration-500 overflow-hidden"
              data-testid={`workforce-trade-${i}`}
            >
              {/* Big translucent count watermark */}
              <div className="pointer-events-none absolute -bottom-4 -end-2 text-6xl md:text-7xl font-black tracking-tighter text-primary/10 group-hover:text-primary/20 transition-colors">
                {trade.count}
              </div>

              <div className="relative">
                <div className="text-2xl md:text-3xl font-black text-primary tabular-nums tracking-tight">
                  {trade.count}
                </div>
                <h4 className="mt-3 text-base md:text-lg font-bold leading-tight">
                  {trade.name}
                </h4>
                <p className="mt-3 text-xs md:text-sm text-secondary-foreground/65 group-hover:text-muted-foreground leading-relaxed">
                  {trade.desc}
                </p>
                <div className="mt-5 h-px w-10 bg-primary/60 group-hover:w-20 transition-all duration-500" />
              </div>
            </motion.article>
          ))}
        </div>

        {/* Footer call strip */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 0.55 }}
          className="mt-10 flex flex-wrap items-center justify-between gap-6 p-6 md:p-8 border border-primary/30 bg-primary/[0.04]"
          data-testid="workforce-call-strip"
        >
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 grid place-items-center bg-primary text-primary-foreground">
              <Clock className="h-5 w-5" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-primary font-bold">
                {w.callTitle}
              </div>
              <a
                href="tel:+966500338215"
                className="mt-1 block text-xl md:text-2xl font-bold hover:text-primary transition-colors tabular-nums"
                data-testid="workforce-call-number"
              >
                +966 50 033 8215
              </a>
            </div>
          </div>

          <a
            href="#contact"
            className="inline-flex items-center gap-3 px-6 py-3 bg-primary text-primary-foreground text-sm font-semibold uppercase tracking-[0.18em] hover:bg-primary/90 transition-colors group"
            data-testid="workforce-bottom-cta"
          >
            {w.cta}
            <ArrowRight className={`h-4 w-4 transition-transform ${isRtl ? "scale-x-[-1] group-hover:-translate-x-1" : "group-hover:translate-x-1"}`} />
          </a>
        </motion.div>
      </div>
    </section>
  );
}
